<template>
  <AdaptiveSheet v-model:opened="opened">
    <f7-page-content>
      <f7-block-title>{{ $t('payout.initiateTitle') }}</f7-block-title>

      <f7-list inset strong outline dividers media-list>
        <f7-list-input
          :label="$t('payout.recipient')"
          type="select"
          :value="recipientId"
          @change="recipientId = $event.target.value"
        >
          <option value="" disabled>{{ $t('payout.selectRecipient') }}</option>
          <option v-for="m in members" :key="m.userId" :value="m.userId">
            {{ m.name }}
          </option>
        </f7-list-input>

        <f7-list-input
          :label="$t('payout.amount')"
          type="text"
          inputmode="decimal"
          :placeholder="$t('payout.amountPlaceholder')"
          :value="amount"
          @input="amount = $event.target.value"
        />

        <f7-list-input
          :label="$t('payout.currency')"
          type="select"
          :value="currency"
          @change="currency = $event.target.value"
        >
          <option v-for="c in CURRENCIES" :key="c" :value="c">{{ c }}</option>
        </f7-list-input>

        <f7-list-input
          :label="$t('payout.reason')"
          type="textarea"
          :placeholder="$t('payout.reasonPlaceholder')"
          :value="reason"
          @input="reason = $event.target.value"
        />
      </f7-list>

      <f7-block class="quorum-hint">
        {{ $t('payout.quorumHint', { count: quorumCount }) }}
      </f7-block>

      <f7-block>
        <f7-button raised large round fill :loading="submitting" @click="submit">
          {{ $t('payout.submitRequest') }}
        </f7-button>
      </f7-block>
    </f7-page-content>
  </AdaptiveSheet>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../../js/i18n.js'
import AdaptiveSheet from '../common/AdaptiveSheet.vue'
import { usePayoutStore } from '../../js/pinia-stores/payout.js'

const props = defineProps({
  opened: { type: Boolean, default: false },
  groupId: { type: String, required: true },
  campaignId: { type: String, default: null },
  cycleId: { type: String, default: null },
  members: { type: Array, default: () => [] },
  adminCount: { type: Number, default: 0 },
  viewerId: { type: String, required: true },
  checkAuth: { type: Function, default: null },
})
const emit = defineEmits(['update:opened', 'initiated'])

const CURRENCIES = ['XOF', 'XAF', 'GHS', 'NGN', 'EUR', 'USD']

const payoutStore = usePayoutStore()
const recipientId = ref('')
const amount = ref('')
const currency = ref('XOF')
const reason = ref('')
const submitting = ref(false)

const opened = computed({
  get: () => props.opened,
  set: (v) => emit('update:opened', v),
})

const quorumCount = computed(() => Math.max(0, props.adminCount - 1))

watch(() => props.opened, (open) => {
  if (open) {
    recipientId.value = ''
    amount.value = ''
    currency.value = 'XOF'
    reason.value = ''
  }
})

async function gate() {
  if (typeof props.checkAuth !== 'function') return true
  return props.checkAuth({ reason: i18n.global.t('payout.initiateReason') })
}

async function submit() {
  const value = Number(String(amount.value).replace(',', '.'))
  if (!recipientId.value) {
    f7.dialog.alert(i18n.global.t('payout.selectRecipientRequired'))
    return
  }
  if (!Number.isFinite(value) || value <= 0) {
    f7.dialog.alert(i18n.global.t('payout.amountRequired'))
    return
  }
  if (!(await gate())) return

  submitting.value = true
  try {
    const fresh = await payoutStore.initiate({
      groupId: props.groupId,
      campaignId: props.campaignId,
      cycleId: props.cycleId,
      initiatedBy: props.viewerId,
      recipientId: recipientId.value,
      amount: value,
      currency: currency.value,
      reason: reason.value.trim() || null,
    })
    submitting.value = false
    opened.value = false
    f7.toast.show({ text: i18n.global.t('payout.initiatedToast'), closeTimeout: 1500 })
    emit('initiated', fresh)
  } catch (e) {
    submitting.value = false
    f7.dialog.alert(e?.response?.data?.message || i18n.global.t('payout.actionFailed'))
  }
}
</script>

<style scoped>
.quorum-hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-align: center;
}
</style>
